import { NODE_LABELS, type NodeLabel } from '@atbash-labs/ontology-core'
import { plantHref, resolvePlantTab, type PlantTab } from '@atbash-labs/plantview-links'
import type {
  PlantSessionMarkEntry,
  PlantSessionMarkView
} from '@axilon-apps/domains-ontology-ui-state'

/**
 * Labels whose `plantHref` falls through to `/plant/entity` (Factory chrome)
 * but belong on a dedicated tab's index surface.
 */
const TAB_OVERRIDE_BY_LABEL: Partial<Record<NodeLabel, PlantTab>> = {
  Alarm: 'alarms',
  ScheduleProfile: 'schedules',
  Holiday: 'schedules',
  Tag: 'compute',
  TagProvider: 'compute',
  Folder: 'compute',
  Database: 'compute'
}

const NODE_LABEL_SET: ReadonlySet<string> = new Set(NODE_LABELS)

/** Map a graph label to the Plant Viewer tab that surfaces it. */
export function plantTabForLabel(label: string): PlantTab | undefined {
  if (!NODE_LABEL_SET.has(label)) return undefined
  const nodeLabel = label as NodeLabel
  const override = TAB_OVERRIDE_BY_LABEL[nodeLabel]
  if (override) return override
  return resolvePlantTab(plantHref('urn:paint:tab', nodeLabel))
}

/**
 * Tabs that currently have visible session paint (respects section filter /
 * paint-off). Model lights when any mark is visible.
 */
export function paintedPlantTabs(
  markById: ReadonlyMap<string, PlantSessionMarkEntry>,
  markFor: (id: string) => PlantSessionMarkView | undefined
): ReadonlySet<PlantTab> {
  const tabs = new Set<PlantTab>()
  for (const [id, entry] of markById) {
    const view = markFor(id)
    if (!view || view === 'dimmed') continue
    tabs.add('model')
    for (const label of entry.labels) {
      const tab = plantTabForLabel(label)
      if (tab) tabs.add(tab)
    }
  }
  return tabs
}
